/**
 * Leviathan Web - Price Book Form Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { GeometricPanel } from '@components/ui/GeometricPanel';
import { API_URL } from '@/config/api';

interface Product {
  id: string;
  sku: string;
  name: string;
  costPerUnit: number;
  pricePerUnit: number;
  category: string;
  unit: string;
}

interface PriceEntry {
  productId: string;
  fixedPrice: number | null;
  rackPlusAmount: number | null;
  marginPercent: number | null;
  costPlusAmount: number | null;
  minPrice: number | null;
  maxPrice: number | null;
}

const PRICING_METHODS = [
  { value: 'FIXED', label: 'Fixed Price', description: 'Set a specific price for each product' },
  { value: 'RACK_PLUS', label: 'Rack + Amount', description: 'Add amount to daily rack price' },
  {
    value: 'MARGIN_PERCENT',
    label: 'Margin %',
    description: 'Markup percentage over product cost',
  },
  { value: 'COST_PLUS', label: 'Cost + Amount', description: 'Add fixed amount to product cost' },
];

const PriceBookFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEdit = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    pricingMethod: 'FIXED',
    isDefault: false,
    isActive: true,
    effectiveDate: '',
    expirationDate: '',
  });

  const [priceEntries, setPriceEntries] = useState<PriceEntry[]>([]);

  useEffect(() => {
    fetchProducts();
    if (isEdit) {
      fetchPriceBook();
    }
  }, [id]);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory?isActive=true`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        // Filter to just fuel products
        const fuelProducts = data.data.filter(
          (p: Product) => p.category === 'FUEL' || p.category === 'ADDITIVE'
        );
        setProducts(fuelProducts);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchPriceBook = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/pricebooks/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        const pb = data.priceBook;
        setFormData({
          name: pb.name,
          description: pb.description || '',
          pricingMethod: pb.pricingMethod,
          isDefault: pb.isDefault,
          isActive: pb.isActive,
          effectiveDate: pb.effectiveDate ? pb.effectiveDate.split('T')[0] : '',
          expirationDate: pb.expirationDate ? pb.expirationDate.split('T')[0] : '',
        });
        setPriceEntries(
          pb.prices.map((p: any) => ({
            productId: p.productId,
            fixedPrice: p.fixedPrice,
            rackPlusAmount: p.rackPlusAmount,
            marginPercent: p.marginPercent,
            costPlusAmount: p.costPlusAmount,
            minPrice: p.minPrice,
            maxPrice: p.maxPrice,
          }))
        );
      }
    } catch (error) {
      setError('Failed to load price book');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name) {
      setError('Price book name is required');
      return;
    }

    setIsSaving(true);

    try {
      const payload = {
        ...formData,
        effectiveDate: formData.effectiveDate || null,
        expirationDate: formData.expirationDate || null,
        prices: priceEntries.filter((e) => e.productId),
      };

      const response = await fetch(`${API_URL}/api/pricebooks${isEdit ? `/${id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        navigate(`/pricing/pricebooks/${data.priceBook.id}`);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to save price book');
      }
    } catch (error) {
      setError('Failed to save price book');
    } finally {
      setIsSaving(false);
    }
  };

  const addAllProducts = () => {
    const existingIds = new Set(priceEntries.map((e) => e.productId));
    const newEntries = products
      .filter((p) => !existingIds.has(p.id))
      .map((p) => ({
        productId: p.id,
        fixedPrice: formData.pricingMethod === 'FIXED' ? Number(p.pricePerUnit) : null,
        rackPlusAmount: formData.pricingMethod === 'RACK_PLUS' ? 0.1 : null,
        marginPercent: formData.pricingMethod === 'MARGIN_PERCENT' ? 15 : null,
        costPlusAmount: formData.pricingMethod === 'COST_PLUS' ? 0.1 : null,
        minPrice: null,
        maxPrice: null,
      }));
    setPriceEntries([...priceEntries, ...newEntries]);
  };

  const removeEntry = (productId: string) => {
    setPriceEntries(priceEntries.filter((e) => e.productId !== productId));
  };

  const updateEntry = (productId: string, field: string, value: number | null) => {
    setPriceEntries(
      priceEntries.map((e) => (e.productId === productId ? { ...e, [field]: value } : e))
    );
  };

  const getPriceField = () => {
    switch (formData.pricingMethod) {
      case 'FIXED':
        return 'fixedPrice';
      case 'RACK_PLUS':
        return 'rackPlusAmount';
      case 'MARGIN_PERCENT':
        return 'marginPercent';
      case 'COST_PLUS':
        return 'costPlusAmount';
      default:
        return 'fixedPrice';
    }
  };

  const getPriceLabel = () => {
    switch (formData.pricingMethod) {
      case 'FIXED':
        return 'Price';
      case 'RACK_PLUS':
        return 'Rack +';
      case 'MARGIN_PERCENT':
        return 'Margin %';
      case 'COST_PLUS':
        return 'Cost +';
      default:
        return 'Price';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-text-tertiary font-mono animate-pulse">&gt; Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6">
          <div className="flex items-center justify-between">
            <h1
              className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
              style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
            >
              {isEdit ? 'Edit Price Book' : 'New Price Book'}
            </h1>
            <button
              onClick={() => navigate('/pricing/pricebooks')}
              className="text-text-secondary hover:text-amber font-mono text-sm uppercase tracking-wider"
            >
              ← Back
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-error-red/10 border border-error-red/50 text-error-red p-4 font-mono text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <GeometricPanel>
          <h3 className="text-lg font-display text-text-primary uppercase tracking-wide mb-4 pb-3 border-b border-amber">
            Price Book Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono"
                placeholder="e.g., Fleet Pricing"
                required
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Pricing Method *
              </label>
              <select
                value={formData.pricingMethod}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, pricingMethod: e.target.value }))
                }
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono"
              >
                {PRICING_METHODS.map((method) => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
              <p className="text-text-tertiary text-xs font-mono mt-1">
                {PRICING_METHODS.find((m) => m.value === formData.pricingMethod)?.description}
              </p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                rows={2}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono"
                placeholder="Optional description..."
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Effective Date
              </label>
              <input
                type="date"
                value={formData.effectiveDate}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, effectiveDate: e.target.value }))
                }
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Expiration Date
              </label>
              <input
                type="date"
                value={formData.expirationDate}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, expirationDate: e.target.value }))
                }
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono"
              />
            </div>

            <div className="flex items-center space-x-6">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, isDefault: e.target.checked }))
                  }
                  className="w-4 h-4 bg-background border-panel-border text-amber focus:ring-amber"
                />
                <span className="text-text-primary font-mono text-sm">Default Price Book</span>
              </label>

              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, isActive: e.target.checked }))
                  }
                  className="w-4 h-4 bg-background border-panel-border text-amber focus:ring-amber"
                />
                <span className="text-text-primary font-mono text-sm">Active</span>
              </label>
            </div>
          </div>
        </GeometricPanel>

        {/* Product Prices */}
        <GeometricPanel>
          <div className="flex justify-between items-center mb-4 pb-3 border-b border-amber">
            <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
              Product Prices
            </h3>
            <button
              type="button"
              onClick={addAllProducts}
              className="text-amber hover:text-amber/80 font-mono text-sm"
            >
              + Add All Products
            </button>
          </div>

          {priceEntries.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-panel-border">
              <p className="text-text-tertiary font-mono">No products added yet</p>
              <button
                type="button"
                onClick={addAllProducts}
                className="text-amber hover:text-amber/80 font-mono text-sm mt-2"
              >
                + Add All Products
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-panel-border">
                  <tr>
                    <th className="text-left px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Product
                    </th>
                    <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Cost
                    </th>
                    <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      {getPriceLabel()}
                    </th>
                    <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Min
                    </th>
                    <th className="text-right px-4 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono">
                      Max
                    </th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {priceEntries.map((entry) => {
                    const product = products.find((p) => p.id === entry.productId);
                    if (!product) return null;

                    const priceField = getPriceField();
                    const priceValue = entry[priceField as keyof PriceEntry] as number | null;

                    return (
                      <tr key={entry.productId} className="border-b border-panel-border">
                        <td className="px-4 py-3">
                          <div className="text-text-primary font-mono">{product.name}</div>
                          <div className="text-text-tertiary text-xs font-mono">{product.sku}</div>
                        </td>
                        <td className="px-4 py-3 text-right text-text-secondary font-mono">
                          ${Number(product.costPerUnit).toFixed(4)}
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={priceValue ?? ''}
                            onChange={(e) =>
                              updateEntry(
                                entry.productId,
                                priceField,
                                e.target.value ? parseFloat(e.target.value) : null
                              )
                            }
                            step={formData.pricingMethod === 'MARGIN_PERCENT' ? '0.1' : '0.0001'}
                            className="w-28 bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={entry.minPrice ?? ''}
                            onChange={(e) =>
                              updateEntry(
                                entry.productId,
                                'minPrice',
                                e.target.value ? parseFloat(e.target.value) : null
                              )
                            }
                            step="0.01"
                            placeholder="-"
                            className="w-24 bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={entry.maxPrice ?? ''}
                            onChange={(e) =>
                              updateEntry(
                                entry.productId,
                                'maxPrice',
                                e.target.value ? parseFloat(e.target.value) : null
                              )
                            }
                            step="0.01"
                            placeholder="-"
                            className="w-24 bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => removeEntry(entry.productId)}
                            className="text-error-red hover:text-error-red/80"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </GeometricPanel>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/pricing/pricebooks')}
            className="bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-2 px-6 transition-all uppercase tracking-wider text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="bg-amber hover:bg-amber/90 text-background font-mono py-2 px-6 transition-all uppercase tracking-wider text-sm disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : isEdit ? 'Update Price Book' : 'Create Price Book'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PriceBookFormPage;
